import { CACHE_TYPE, type CacheConfig, type CacheEntry, type CacheLookupResult } from '../types';

const SOURCE_PREFIX_LEN = 32;
const CACHE_KEY_PREFIX = 'alitranslate_';
const DEFAULT_MAX_ENTRIES = 2000;

interface ICacheStorage {
  get(key: string): CacheEntry | null;
  set(key: string, entry: CacheEntry): void;
  delete(key: string): void;
  clear(): void;
}

class MemoryCacheStorage implements ICacheStorage {
  private store = new Map<string, CacheEntry>();

  get(key: string): CacheEntry | null {
    return this.store.get(key) ?? null;
  }

  set(key: string, entry: CacheEntry): void {
    this.store.set(key, entry);
  }

  delete(key: string): void {
    this.store.delete(key);
  }

  clear(): void {
    this.store.clear();
  }
}

class WebCacheStorage implements ICacheStorage {
  private backend: Storage;
  private maxEntries: number;

  constructor(backend: Storage, maxEntries = DEFAULT_MAX_ENTRIES) {
    this.backend = backend;
    this.maxEntries = maxEntries;
  }

  get(key: string): CacheEntry | null {
    try {
      const raw = this.backend.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw) as CacheEntry;
    } catch {
      return null;
    }
  }

  set(key: string, entry: CacheEntry): void {
    try {
      this.evictIfNeeded();
      this.backend.setItem(key, JSON.stringify(entry));
    } catch {
      // Storage full or unavailable
    }
  }

  delete(key: string): void {
    try {
      this.backend.removeItem(key);
    } catch {
      // Ignore
    }
  }

  clear(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < this.backend.length; i++) {
        const key = this.backend.key(i);
        if (key?.startsWith(CACHE_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(k => this.backend.removeItem(k));
    } catch {
      // Ignore
    }
  }

  private evictIfNeeded(): void {
    const entries: Array<{ key: string; timestamp: number }> = [];

    for (let i = 0; i < this.backend.length; i++) {
      const key = this.backend.key(i);
      if (!key?.startsWith(CACHE_KEY_PREFIX)) continue;
      try {
        const raw = this.backend.getItem(key);
        if (raw) {
          const entry = JSON.parse(raw) as CacheEntry;
          entries.push({ key, timestamp: entry.timestamp });
        }
      } catch {
        // Ignore corrupt entries
      }
    }

    if (entries.length < this.maxEntries) return;

    entries.sort((a, b) => a.timestamp - b.timestamp);
    const toRemove = entries.slice(0, Math.ceil(entries.length * 0.2));
    toRemove.forEach(e => this.backend.removeItem(e.key));
  }
}

function hashText(text: string): string {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return hash.toString(36);
}

function getCacheKey(text: string, srcLang: string, tgtLang: string): string {
  return `${CACHE_KEY_PREFIX}${srcLang}_${tgtLang}_${hashText(text)}`;
}

export class CacheManager {
  private config: CacheConfig;
  private storage: ICacheStorage;

  constructor(config: CacheConfig) {
    this.config = config;
    this.storage = this.createStorage(config.type);
  }

  private createStorage(type: CACHE_TYPE): ICacheStorage {
    switch (type) {
      case CACHE_TYPE.SESSION_STORAGE:
        try {
          return new WebCacheStorage(sessionStorage);
        } catch {
          return new MemoryCacheStorage();
        }
      case CACHE_TYPE.LOCAL_STORAGE:
        try {
          return new WebCacheStorage(localStorage);
        } catch {
          return new MemoryCacheStorage();
        }
      case CACHE_TYPE.TEMPORARY:
      default:
        return new MemoryCacheStorage();
    }
  }

  lookup(texts: string[], srcLang: string, tgtLang: string): CacheLookupResult {
    const hits = new Map<number, string>();
    const misses: Array<{ index: number; text: string }> = [];

    if (!this.config.enabled) {
      texts.forEach((text, index) => misses.push({ index, text }));
      return { hits, misses };
    }

    for (let i = 0; i < texts.length; i++) {
      const text = texts[i];
      const key = getCacheKey(text, srcLang, tgtLang);
      const entry = this.storage.get(key);

      if (entry && !this.isExpired(entry) && this.verifyPrefix(entry, text)) {
        hits.set(i, entry.translation);
      } else {
        misses.push({ index: i, text });
      }
    }

    return { hits, misses };
  }

  store(
    entries: Array<{ text: string; translation: string }>,
    srcLang: string,
    tgtLang: string,
  ): void {
    if (!this.config.enabled) return;

    for (const { text, translation } of entries) {
      const key = getCacheKey(text, srcLang, tgtLang);
      this.storage.set(key, {
        translation,
        timestamp: Date.now(),
        sourcePrefix: text.slice(0, SOURCE_PREFIX_LEN),
      });
    }
  }

  set(source: string, targets?: Record<string, string>): void;
  set(terms: Array<{ source: string; targets?: Record<string, string> }>): void;
  set(
    sourceOrTerms: string | Array<{ source: string; targets?: Record<string, string> }>,
    targets?: Record<string, string>,
  ): void {
    if (typeof sourceOrTerms === 'string') {
      if (targets) {
        for (const [lang, translation] of Object.entries(targets)) {
          const key = getCacheKey(sourceOrTerms, 'auto', lang);
          this.storage.set(key, {
            translation,
            timestamp: Date.now(),
            sourcePrefix: sourceOrTerms.slice(0, SOURCE_PREFIX_LEN),
          });
        }
      }
    } else {
      for (const term of sourceOrTerms) {
        if (term.targets) {
          this.set(term.source, term.targets);
        }
      }
    }
  }

  clear(): void {
    this.storage.clear();
  }

  private isExpired(entry: CacheEntry): boolean {
    if (this.config.expireTime === -1) return false;
    return Date.now() - entry.timestamp > this.config.expireTime;
  }

  private verifyPrefix(entry: CacheEntry, text: string): boolean {
    if (!entry.sourcePrefix) return true;
    return entry.sourcePrefix === text.slice(0, SOURCE_PREFIX_LEN);
  }
}
